import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api.js';

export default function JoinForm() {
  const { token, channel } = useParams();
  // /r/:token (legacy / direct) vs /r/:channel/:token (channel-aware).
  const apiPath = channel ? `/api/public/r/${channel}/${token}` : apiPath;
  const [phase, setPhase] = useState('loading');
  const [err, setErr] = useState('');
  const [form, setForm]   = useState(null);   // schema (fields, consent_label, thank_you_text)
  const [values, setValues] = useState({});
  const [consent, setConsent] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(apiPath).then(r => {
      if (r.redirect && r.url) { window.location.replace(r.url); return; }
      if (r.thank_you) { setPhase('done'); return; }
      const schema = r.form;
      const prefill = r.prefill || {};
      const initial = {};
      for (const f of schema.fields || []) {
        initial[f.key] = prefill[f.key] != null ? String(prefill[f.key]) : '';
        // strip leading 91 from prefilled phone for friendlier UX
        if (f.type === 'tel' && initial[f.key].startsWith('91') && initial[f.key].length === 12) {
          initial[f.key] = initial[f.key].slice(2);
        }
      }
      setForm(schema);
      setValues(initial);
      setPhase('ready');
    }).catch(e => {
      if (e.status === 410) setPhase('expired');
      else if (e.status === 409) setPhase('used');
      else if (e.status === 404) setPhase('notfound');
      else { setErr(e.message); setPhase('error'); }
    });
  }, [token]);

  function set(key, v) { setValues(s => ({ ...s, [key]: v })); }

  async function submit(e) {
    e.preventDefault();
    setErr('');
    if (!form) return;

    // Required check
    for (const f of form.fields) {
      if (f.required && !String(values[f.key] || '').trim()) {
        setErr(`Please fill: ${f.label}`); return;
      }
    }
    // Phone normalization for any tel field — prepend 91 if 10 digits
    const submission = { ...values, consent: consent ? 1 : 0 };
    for (const f of form.fields) {
      if (f.type === 'tel') {
        const digits = String(submission[f.key] || '').replace(/\D/g, '');
        if (digits.length < 10) { setErr(`Invalid phone: ${f.label}`); return; }
        submission[f.key] = digits.length === 10 ? '91' + digits : digits;
      }
    }

    setSubmitting(true);
    try {
      await api.post(apiPath, submission);
      setPhase('done');
    } catch (e) {
      if (e.status === 409) setPhase('used');
      else setErr(e.message || 'Submit failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-xl mx-auto px-5 py-10">
        <header className="text-center mb-8">
          <img src="/iaapi-logo.png" alt="IAAPI" className="h-12 w-auto mx-auto" />
          <div className="mt-2 text-xs text-slate-500">Indian Association of Amusement Parks &amp; Industries</div>
        </header>

        {phase === 'loading'  && <div className="text-center text-sm text-slate-400 py-10">Loading your details…</div>}
        {phase === 'notfound' && <Card title="Link not recognised" body="This link may have been mistyped or invalidated. Please check your WhatsApp or email message and try again." />}
        {phase === 'expired'  && <Card title="Link expired" body="This invitation link has expired. Please reach out to IAAPI for a fresh link." />}
        {phase === 'used'     && <Card title="Already submitted" body="Your details are already with us. Our membership team will reach out shortly." />}
        {phase === 'error'    && <Card title="Something went wrong" body={err || 'Please try again in a moment.'} />}
        {phase === 'done'     && <Card title="Thank you!" body={form?.thank_you_text || 'Your details are in.'} />}

        {phase === 'ready' && form && (
          <form onSubmit={submit} className="card p-6">
            <h2 className="text-lg font-semibold text-brand-700">{form.name}</h2>
            <p className="text-sm text-slate-600 mt-1.5 mb-5">{form.description || 'Confirm or edit your details below.'}</p>

            {form.fields.map(f => (
              <FieldRow key={f.key} field={f} value={values[f.key] || ''} onChange={v => set(f.key, v)} />
            ))}

            {form.consent_label && (
              <label className="flex items-start gap-2.5 mt-3 mb-1 text-sm text-slate-600">
                <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-1" />
                <span>{form.consent_label}</span>
              </label>
            )}

            {err && <div className="mt-2 text-sm text-rose-600">{err}</div>}
            <button className="btn w-full mt-4 py-3 text-base" disabled={submitting}>{submitting ? 'Submitting…' : 'Submit'}</button>
          </form>
        )}

        <footer className="mt-10 text-center text-[11px] text-slate-400">
          © IAAPI · Indian Association of Amusement Parks &amp; Industries
        </footer>
      </div>
    </div>
  );
}

function FieldRow({ field, value, onChange }) {
  const required = !!field.required;
  return (
    <div className="mb-4">
      <div className="label-l">{field.label}{required && <span className="text-rose-500"> *</span>}</div>
      {field.type === 'select' ? (
        <select className="input w-full" value={value} onChange={e => onChange(e.target.value)} required={required}>
          <option value="">Select…</option>
          {(field.options || []).map(opt => <option key={opt}>{opt}</option>)}
        </select>
      ) : field.type === 'textarea' ? (
        <textarea className="input w-full h-24" value={value} onChange={e => onChange(e.target.value)} required={required} />
      ) : field.type === 'tel' ? (
        <div className="flex">
          <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-slate-200 bg-slate-50 text-sm text-slate-500">+91</span>
          <input className="input rounded-l-none w-full"
                 value={value}
                 onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 12))}
                 inputMode="tel" required={required} />
        </div>
      ) : (
        <input
          className="input w-full"
          type={field.type === 'email' ? 'email' : field.type === 'number' ? 'number' : 'text'}
          value={value}
          onChange={e => onChange(e.target.value)}
          required={required}
        />
      )}
    </div>
  );
}

function Card({ title, body }) {
  return (
    <div className="card p-7 text-center">
      <h2 className="text-lg font-semibold text-brand-700">{title}</h2>
      <p className="mt-2 text-sm text-slate-600">{body}</p>
    </div>
  );
}
